import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "../api/client";
import type {
  OpportunityFieldDefinition,
  OpportunityFieldValueDocument,
  OpportunityFieldsLoader,
  OpportunityManualValueWriteCapability,
} from "../api/client";
import { fieldErrorText, parseOpportunityFieldValue } from "./opportunityFields";

const inputClass = "rounded border border-border bg-background px-3 py-2 text-sm";
const MAX_NUMBER = 1_000_000_000_000_000;

function normalizedFieldText(
  definition: OpportunityFieldDefinition,
  raw: string | number | undefined,
): string {
  const text = raw === undefined ? "" : String(raw).trim();
  if (definition.type !== "number" || text === "") return text;
  const numeric = Number(text);
  if (!Number.isFinite(numeric) || Math.abs(numeric) > MAX_NUMBER) return text;
  return String(numeric);
}

function conflictFieldsFor(
  definitions: OpportunityFieldDefinition[],
  server: Record<string, string>,
  baseline: Record<string, string>,
  draft: Record<string, string>,
): string[] {
  const fields: string[] = [];
  for (const definition of definitions) {
    if (!Object.prototype.hasOwnProperty.call(draft, definition.id)) continue;
    const draftText = normalizedFieldText(definition, draft[definition.id]);
    const baselineText = normalizedFieldText(definition, baseline[definition.id] ?? "");
    const serverText = normalizedFieldText(definition, server[definition.id] ?? "");
    // A conflict is a server value that moved away from the baseline while the
    // operator's draft does not explicitly hold that new value. A draft reverted
    // to the old baseline is still a conflict: it must not clear the lock and let
    // Save quietly send the stale baseline over the teammate's newer value.
    // Typing the CURRENT server value clears it by adopting that value explicitly.
    // Comparing normalized text means "1.50" vs 1.5 and " abc " vs "abc" are equal.
    if (serverText !== baselineText && draftText !== serverText) {
      fields.push(definition.id);
    }
  }
  return fields;
}


export function OpportunityManualValues({
  projectId,
  sourceJobId,
  sourceDecisionsHash,
  organizationId,
  definitions,
  document,
  writeCapability,
  canManage,
  loader,
  onSaved,
}: {
  projectId: string;
  sourceJobId: string;
  sourceDecisionsHash: string;
  organizationId: string;
  definitions: OpportunityFieldDefinition[];
  document: OpportunityFieldValueDocument | undefined;
  writeCapability: OpportunityManualValueWriteCapability | null;
  canManage: boolean;
  loader: OpportunityFieldsLoader;
  onSaved: (document: OpportunityFieldValueDocument) => void;
}) {
  const active = useMemo(
    () => definitions.filter((definition) => !definition.archived),
    [definitions],
  );
  const archived = useMemo(
    () => definitions.filter((definition) => definition.archived),
    [definitions],
  );
  const activeIds = useMemo(() => new Set(active.map((item) => item.id)), [active]);
  const current = useMemo(() => {
    const map = new Map<string, string | number>();
    for (const item of document?.values ?? []) map.set(item.field_id, item.value);
    return map;
  }, [document]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [catalogueWarning, setCatalogueWarning] = useState("");
  const [conflictFields, setConflictFields] = useState<string[]>([]);
  const writeSupported = writeCapability?.supported === true;
  const writeUnsupported =
    writeCapability !== null && writeCapability.supported === false;
  const generation = useRef(0);
  const draftRef = useRef<Record<string, string>>({});
  const baselineRef = useRef<Record<string, string>>({});
  const serverRef = useRef<Record<string, string>>({});
  const conflictRef = useRef<string[]>([]);
  const typesRef = useRef<Record<string, string>>({});
  const identityRef = useRef("");
  const documentIdRef = useRef("new");
  const documentVersionRef = useRef(0);
  const identityKey =
    `${projectId}\u0000${sourceJobId}\u0000${sourceDecisionsHash}\u0000${organizationId}`;
  const documentId = document?.id ?? "new";
  const documentVersion = document?.version ?? 0;

  useEffect(() => {
    // Keep the synchronous mirror current so a catalogue render can compare the
    // operator's latest keystroke, not only the value from the previous sync.
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    const server: Record<string, string> = {};
    for (const definition of active) {
      const value = current.get(definition.id);
      server[definition.id] = value === undefined ? "" : String(value);
    }
    const identityChanged =
      identityRef.current !== identityKey || documentIdRef.current !== documentId;
    const previousDraft = draftRef.current;
    const previousBaseline = baselineRef.current;
    const previousConflict = conflictRef.current;
    const previousTypes = typesRef.current;
    const nextDraft: Record<string, string> = {};
    const nextBaseline: Record<string, string> = {};
    const warnings: string[] = [];

    if (identityChanged) {
      Object.assign(nextDraft, server);
      Object.assign(nextBaseline, server);
    } else {
      const activeIds = new Set(active.map((definition) => definition.id));
      for (const definition of active) {
        const serverValue = server[definition.id] ?? "";
        const hadDraft = Object.prototype.hasOwnProperty.call(previousDraft, definition.id);
        const oldType = previousTypes[definition.id];
        const oldBaseline = previousBaseline[definition.id] ?? "";
        const draftValue = previousDraft[definition.id] ?? "";
        const dirty =
          hadDraft &&
          normalizedFieldText(definition, draftValue) !==
            normalizedFieldText(definition, oldBaseline);
        const serverMoved =
          normalizedFieldText(definition, serverValue) !==
          normalizedFieldText(definition, oldBaseline);
        const draftMatchesServer =
          normalizedFieldText(definition, draftValue) ===
          normalizedFieldText(definition, serverValue);
        if (oldType !== undefined && oldType !== definition.type) {
          // Type is immutable server-side, but a malformed/foreign catalogue must
          // fail visibly rather than turn an old draft into a value of another type.
          warnings.push(
            `Polje „${definition.label}“ promijenilo je tip; prethodni unos nije primijenjen.`,
          );
          nextDraft[definition.id] = serverValue;
          nextBaseline[definition.id] = serverValue;
        } else if (!hadDraft) {
          // A newly added active field is initialized from its saved value only.
          nextDraft[definition.id] = serverValue;
          nextBaseline[definition.id] = serverValue;
        } else if (!dirty && previousConflict.includes(definition.id)
          && serverMoved && !draftMatchesServer) {
          // The operator undid an edit back to the old baseline while a conflict
          // was already open. A same-version refresh must not silently adopt the
          // server value and erase their explicit reconciliation choice; keep the
          // conflict (and Save locked) until keep-mine/take-server or typing the
          // current server value.
          nextDraft[definition.id] = previousDraft[definition.id];
          nextBaseline[definition.id] = oldBaseline;
        } else if (!dirty) {
          // A clean field follows the validated server post-image. A never-edited
          // clean field is not a reconciliation decision.
          nextDraft[definition.id] = serverValue;
          nextBaseline[definition.id] = serverValue;
        } else if (!serverMoved) {
          // Preserve the operator's unsaved edit across catalogue refreshes.
          nextDraft[definition.id] = previousDraft[definition.id];
          nextBaseline[definition.id] = serverValue;
        } else if (draftMatchesServer) {
          // An explicit edit back to the current server value takes that value;
          // the draft is not silently rebased while it still differs.
          nextDraft[definition.id] = serverValue;
          nextBaseline[definition.id] = serverValue;
        } else {
          // A genuine conflict keeps the old baseline so it stays visible and
          // Save stays blocked until the operator makes an explicit choice.
          nextDraft[definition.id] = previousDraft[definition.id];
          nextBaseline[definition.id] = oldBaseline;
        }
      }
      const removedDirty = Object.keys(previousDraft).filter(
        (fieldId) =>
          !activeIds.has(fieldId) &&
          previousDraft[fieldId] !== (previousBaseline[fieldId] ?? ""),
      );
      if (removedDirty.length > 0) {
        warnings.push(
          "Neka polja su arhivirana ili uklonjena; nespremljeni unosi za njih nisu primijenjeni. Sačuvane vrijednosti ostaju vidljive.",
        );
      }
    }

    draftRef.current = nextDraft;
    baselineRef.current = nextBaseline;
    serverRef.current = { ...server };
    typesRef.current = Object.fromEntries(
      active.map((definition) => [definition.id, definition.type]),
    );
    identityRef.current = identityKey;
    documentIdRef.current = documentId;
    documentVersionRef.current = documentVersion;
    const nextConflict = identityChanged
      ? []
      : conflictFieldsFor(active, server, nextBaseline, nextDraft);
    conflictRef.current = nextConflict;
    setDraft(nextDraft);
    setConflictFields(nextConflict);
    if (warnings.length > 0) setCatalogueWarning(warnings.join(" "));
    else if (identityChanged) setCatalogueWarning("");
  }, [active, current, identityKey, documentId, documentVersion]);

  useEffect(() => {
    generation.current += 1;
    setError("");
    setNotice("");
    // A response bound to the previous identity is ignored by the generation
    // guard. Clear the in-flight flag too so a reused instance cannot stay
    // disabled forever if the parent keeps the same element mounted.
    setSaving(false);
    return () => {
      generation.current += 1;
    };
  }, [projectId, sourceJobId, sourceDecisionsHash, organizationId]);

  const archivedValues = (document?.values ?? []).filter(
    (item) => !activeIds.has(item.field_id),
  );
  const conflictLabels = conflictFields.map(
    (fieldId) => active.find((definition) => definition.id === fieldId)?.label ?? fieldId,
  );

  const valueLabel = (definition: OpportunityFieldDefinition, raw: string | number): string => {
    if (definition.type === "select") {
      const option = definition.options.find((item) => item.id === raw);
      if (!option) return String(raw);
      return option.archived ? `${option.label} (arhivirana opcija)` : option.label;
    }
    return String(raw);
  };

  const updateDraft = (fieldId: string, value: string) => {
    const next = { ...draftRef.current, [fieldId]: value };
    draftRef.current = next;
    setDraft(next);
    // Recompute on every keystroke so an explicit edit to the current server
    // value clears a genuine conflict instead of leaving it sticky. Undoing to
    // the old baseline does not clear it: that would let Save send the stale
    // baseline over the teammate's newer value.
    const nextConflict = conflictFieldsFor(
      active, serverRef.current, baselineRef.current, next,
    );
    conflictRef.current = nextConflict;
    setConflictFields(nextConflict);
  };

  const resolveConflict = (takeServer: boolean) => {
    if (conflictFields.length === 0) return;
    const nextDraft = { ...draftRef.current };
    const nextBaseline = { ...baselineRef.current };
    for (const fieldId of conflictFields) {
      const serverValue = serverRef.current[fieldId] ?? "";
      if (takeServer) nextDraft[fieldId] = serverValue;
      // Keep-mine intentionally rebases only the baseline: the operator's draft
      // remains unsaved until they explicitly press Save. Take-server replaces
      // only the conflicting drafts; other unsaved fields stay untouched.
      nextBaseline[fieldId] = serverValue;
    }
    draftRef.current = nextDraft;
    baselineRef.current = nextBaseline;
    const nextConflict = conflictFieldsFor(
      active, serverRef.current, nextBaseline, nextDraft,
    );
    conflictRef.current = nextConflict;
    setDraft(nextDraft);
    setConflictFields(nextConflict);
    setNotice(
      takeServer
        ? "Preuzete su poslužiteljske vrijednosti za polja u sukobu. Ostali nespremljeni unosi zadržani su."
        : "Vaš unos je zadržan, a osnovica je usklađena s najnovijom verzijom. Spremite ga eksplicitno.",
    );
  };

  const save = async () => {
    if (!canManage || saving || !writeSupported) return;
    // Recompute against the synchronous mirrors at click time. The rendered state
    // can lag a keystroke, and an unreconciled server move must never fall through
    // to a patch just because the conflict banner was not painted yet. If a newer
    // document prop has not reached its effect yet, derive the server side from
    // that prop instead of the previous mirror so the move is still detected.
    const documentAdvanced =
      documentId !== documentIdRef.current || documentVersion !== documentVersionRef.current;
    let serverAtSave = serverRef.current;
    if (documentAdvanced) {
      serverAtSave = {};
      for (const definition of active) {
        const value = current.get(definition.id);
        serverAtSave[definition.id] = value === undefined ? "" : String(value);
      }
    }
    const pendingConflicts = conflictFieldsFor(
      active, serverAtSave, baselineRef.current, draftRef.current,
    );
    if (pendingConflicts.length > 0) {
      conflictRef.current = pendingConflicts;
      setConflictFields(pendingConflicts);
      return;
    }
    const expectedVersion = document?.version ?? 0;
    const values = [];
    const savedFieldIds: string[] = [];
    for (const definition of active) {
      const raw = (draftRef.current[definition.id] ?? "").trim();
      // Only an explicit change from the acknowledged baseline is a patch
      // candidate. A field reverted to that baseline after a server move is not a
      // change and must never be sent as a stale overwrite.
      const baselineText = normalizedFieldText(
        definition, baselineRef.current[definition.id] ?? "",
      );
      if (normalizedFieldText(definition, raw) === baselineText) continue;
      const existing = current.get(definition.id);
      const existingText = existing === undefined ? "" : String(existing);
      if (raw === existingText) continue;
      if (raw === "") {
        if (existing !== undefined) {
          values.push({ field_id: definition.id, value: null });
          savedFieldIds.push(definition.id);
        }
        continue;
      }
      if (definition.type === "number") {
        const numeric = Number(raw);
        if (!Number.isFinite(numeric) || Math.abs(numeric) > MAX_NUMBER) {
          setError(`Vrijednost polja „${definition.label}“ mora biti konačan broj.`);
          return;
        }
        values.push({ field_id: definition.id, value: numeric });
        savedFieldIds.push(definition.id);
      } else {
        values.push({ field_id: definition.id, value: raw });
        savedFieldIds.push(definition.id);
      }
    }
    if (values.length === 0) {
      setNotice("Nema promjena za spremanje.");
      return;
    }
    const currentGeneration = generation.current;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const raw = await loader.patchOpportunityFieldValues(projectId, organizationId, {
        version: expectedVersion,
        source_job_id: sourceJobId,
        source_decisions_hash: sourceDecisionsHash,
        values,
      });
      if (currentGeneration !== generation.current) return;
      const parsed = parseOpportunityFieldValue(
        raw, projectId, sourceJobId, sourceDecisionsHash,
      );
      if (
        parsed === null ||
        parsed.organization_id !== organizationId ||
        parsed.version !== expectedVersion + 1
      ) {
        throw new Error("value_response_invalid");
      }
      // Acknowledge our own validated post-image before handing it to the parent:
      // the server canonicalizes text (trim) and numbers (1.50 -> 1.5), so keeping
      // the old raw draft/baseline would make our own success look like a remote
      // edit. Only fields in this request are acknowledged; other dirty drafts and
      // the identity/version guard stay intact.
      const postImage = new Map(
        parsed.values.map((item) => [item.field_id, String(item.value)]),
      );
      const nextDraft = { ...draftRef.current };
      const nextBaseline = { ...baselineRef.current };
      const nextServer = { ...serverRef.current };
      for (const fieldId of savedFieldIds) {
        const value = postImage.get(fieldId) ?? "";
        nextDraft[fieldId] = value;
        nextBaseline[fieldId] = value;
        nextServer[fieldId] = value;
      }
      draftRef.current = nextDraft;
      baselineRef.current = nextBaseline;
      serverRef.current = nextServer;
      conflictRef.current = [];
      documentIdRef.current = parsed.id;
      documentVersionRef.current = parsed.version;
      setDraft(nextDraft);
      setConflictFields([]);
      onSaved(parsed);
      setNotice("Unos tima spremljen je na projekt.");
    } catch (err) {
      if (currentGeneration !== generation.current) return;
      if (err instanceof ApiError && err.detail === "version_conflict") {
        setError(
          "Netko je promijenio vrijednosti ovog zapisa. Osvježite prikaz i ponovno unesite; vaša verzija nije prepisana.",
        );
      } else {
        setError(fieldErrorText(err));
      }
    } finally {
      if (currentGeneration === generation.current) setSaving(false);
    }
  };

  if (!active.length && !archivedValues.length) {
    return (
      <section className="mt-4 rounded border border-border p-3 text-sm"
               data-testid="opportunity-manual-values">
        <h4 className="text-sm font-semibold">Unos tima</h4>
        <p className="mt-1 text-xs text-muted-foreground">
          Projekt još nema timskih polja. Ručne vrijednosti nisu provjereni dokaz.
        </p>
      </section>
    );
  }

  return (
    <section className="mt-4 space-y-2 rounded border border-border bg-muted/20 p-3"
             aria-label="Unos tima" data-testid="opportunity-manual-values">
      <h4 className="text-sm font-semibold">Unos tima</h4>
      <p className="text-xs text-muted-foreground">
        Ručno unesene timske vrijednosti. Nisu provjereni dokaz, ne mijenjaju opis,
        ocjenu, preporuku ni ljudski status i ne daju pravo na odobrenje ili slanje.
      </p>
      {error && (
        <p className="text-sm text-destructive" role="alert"
           data-testid="opportunity-manual-values-error">{error}</p>
      )}
      {notice && (
        <p className="text-sm text-muted-foreground" role="status"
           data-testid="opportunity-manual-values-notice">{notice}</p>
      )}
      {catalogueWarning && (
        <p className="text-sm text-warn" role="status"
           data-testid="opportunity-manual-values-warning">{catalogueWarning}</p>
      )}
      {conflictFields.length > 0 && (
        <div className="rounded border border-warn/60 bg-background p-2"
             data-testid="opportunity-manual-values-conflict">
          <p className="text-sm text-warn" role="status"
             data-testid="opportunity-manual-values-warning">
            Vrijednost polja {conflictLabels.map((label) => `„${label}“`).join(", ")}{" "}
            promijenjena je na poslužitelju. Vaš nespremljeni unos je zadržan. Odaberite
            hoćete li zadržati svoj unos ili preuzeti poslužiteljske vrijednosti.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border border-border px-3 py-2 text-xs disabled:opacity-40"
              data-testid="opportunity-manual-values-conflict-keep"
              disabled={saving}
              onClick={() => resolveConflict(false)}
            >
              Zadrži moj unos
            </button>
            <button
              type="button"
              className="rounded border border-border px-3 py-2 text-xs disabled:opacity-40"
              data-testid="opportunity-manual-values-conflict-take-server"
              disabled={saving}
              onClick={() => resolveConflict(true)}
            >
              Preuzmi poslužiteljske vrijednosti
            </button>
          </div>
        </div>
      )}
      {writeUnsupported && (
        <p className="text-sm text-muted-foreground" role="status"
           data-testid="opportunity-manual-values-readonly-database">
          Ova baza nije MongoDB replica set, pa je spremanje timskih vrijednosti
          onemogućeno. Postojeće vrijednosti, katalog polja i raspored ostaju dostupni
          za čitanje; ništa nije djelomično spremljeno.
        </p>
      )}
      {active.map((definition) => (
        <label key={definition.id} className="block text-sm">
          {definition.label} <span className="text-xs text-muted-foreground">(Unos tima)</span>
          {definition.type === "select" ? (
            <select
              className={`${inputClass} mt-1 block w-full`}
              value={draft[definition.id] ?? ""}
              disabled={!canManage || !writeSupported || saving}
              aria-label={`Unos tima: ${definition.label}`}
              onChange={(event) => updateDraft(definition.id, event.target.value)}
            >
              <option value="">— bez vrijednosti —</option>
              {definition.options.filter((option) => !option.archived).map((option) => (
                <option key={option.id} value={option.id}>{option.label}</option>
              ))}
              {(() => {
                const selected = current.get(definition.id);
                if (typeof selected !== "string") return null;
                const archivedOption = definition.options.find(
                  (option) => option.id === selected && option.archived,
                );
                return archivedOption ? (
                  <option value={archivedOption.id} disabled>
                    {archivedOption.label} (arhivirana opcija)
                  </option>
                ) : null;
              })()}
            </select>
          ) : (
            <input
              className={`${inputClass} mt-1 block w-full`}
              type={definition.type === "number" ? "number" : definition.type === "date" ? "date" : "text"}
              maxLength={definition.type === "text" ? 2000 : undefined}
              value={draft[definition.id] ?? ""}
              disabled={!canManage || !writeSupported || saving}
              aria-label={`Unos tima: ${definition.label}`}
              onChange={(event) => updateDraft(definition.id, event.target.value)}
            />
          )}
        </label>
      ))}
      {archived.length > 0 && (
        <details className="rounded border border-border bg-background p-2 text-xs"
                 data-testid="opportunity-archived-values">
          <summary className="cursor-pointer font-medium">
            Arhivirana polja i sačuvane vrijednosti
          </summary>
          <ul className="mt-2 space-y-1">
            {archived.map((definition) => {
              const value = archivedValues.find((item) => item.field_id === definition.id);
              return (
                <li key={definition.id} data-testid={`opportunity-archived-value-${definition.id}`}>
                  <span className="text-muted-foreground">{definition.label} (arhivirano):</span>{" "}
                  {value === undefined ? "—" : valueLabel(definition, value.value)}
                </li>
              );
            })}
          </ul>
          <p className="mt-1 text-muted-foreground">
            Sačuvane vrijednosti arhiviranih polja ne mogu se mijenjati ni reciklirati.
          </p>
        </details>
      )}
      {canManage && active.length > 0 && (
        <button
          type="button"
          className="rounded bg-primary px-3 py-2 text-sm text-primary-foreground disabled:opacity-40"
          data-testid="opportunity-manual-values-save"
          disabled={!writeSupported || saving || conflictFields.length > 0}
          onClick={() => void save()}
        >
          {saving ? "Spremam unos tima…" : "Spremi unos tima"}
        </button>
      )}
      {!canManage && (
        <p className="text-xs text-muted-foreground" data-testid="opportunity-manual-values-readonly">
          Vrijednosti možete čitati; uređivanje je dopušteno samo vlasniku i
          project_adminu.
        </p>
      )}
    </section>
  );
}
